import React, { useMemo, useState, useCallback } from "react";
import { Box, IconButton, Typography, Divider } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const getPlainText = (content) => {
	if (content == null) return "";
	if (typeof content === "string" || typeof content === "number") return String(content);
	if (Array.isArray(content)) return content.map(getPlainText).join("");
	if (React.isValidElement(content)) return getPlainText(content.props?.children);
	return String(content);
};

/** Shared MUI Tooltip slot props for light, readable cell tooltips */
export const richTooltipSlotProps = {
	tooltip: {
		sx: {
			bgcolor: "#ffffff",
			color: "rgba(0, 0, 0, 0.87)",
			boxShadow: "0 4px 24px rgba(15, 23, 42, 0.14)",
			border: "1px solid",
			borderColor: "divider",
			borderRadius: "10px",
			maxWidth: 480,
			p: 0,
			overflow: "hidden",
		},
	},
	arrow: {
		sx: {
			color: "#ffffff",
			"&::before": {
				border: "1px solid",
				borderColor: "divider",
				backgroundColor: "#ffffff",
				boxSizing: "border-box",
			},
		},
	},
};

export default function CustomTooltipContentWithAction({ text }) {
	const defaultSize = 13;
	const [tooltipSize, setTooltipSize] = useState(defaultSize);
	const [copied, setCopied] = useState(false);

	const plainText = useMemo(() => getPlainText(text), [text]);

	const increaseSize = () => setTooltipSize((prev) => Math.min(prev + 2, 20));
	const decreaseSize = () => setTooltipSize((prev) => (prev > 10 ? prev - 2 : prev));
	const resetSize = () => setTooltipSize(defaultSize);

	const handleStopPropagation = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleCopy = useCallback(async (e) => {
		e.stopPropagation();
		if (!plainText) return;

		try {
			await navigator.clipboard.writeText(plainText);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			const textarea = document.createElement("textarea");
			textarea.value = plainText;
			textarea.style.position = "fixed";
			textarea.style.left = "-9999px";
			document.body.appendChild(textarea);
			textarea.select();
			document.execCommand("copy");
			document.body.removeChild(textarea);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	}, [plainText]);

	const key = useMemo(() => {
		const safeText = String(text || "");
		return safeText?.substring(0, 5) + "tooltip";
	}, [text]);

	const iconButtonSx = {
		color: "text.secondary",
		p: 0.25,
		"&:hover": {
			color: "primary.main",
			bgcolor: "action.hover",
		},
	};

	return (
		<Box
			onClick={handleStopPropagation}
			sx={{
				position: "relative",
				width: "100%",
				minWidth: 200,
				maxWidth: 460,
			}}
			key={key}
		>
			<Box
				onClick={handleStopPropagation}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 0.25,
					px: 1,
					py: 0.5,
					bgcolor: "#f8fafc",
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<IconButton
					aria-label={copied ? "Copied" : "Copy full content"}
					title={copied ? "Copied!" : "Copy full content"}
					onClick={handleCopy}
					size="small"
					sx={{
						...iconButtonSx,
						...(copied && { color: "success.main" }),
					}}
				>
					{copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
				</IconButton>
				<IconButton aria-label="Increase text size" onClick={(e) => { e.stopPropagation(); increaseSize(); }} size="small" sx={iconButtonSx}>
					<ZoomInIcon sx={{ fontSize: 16 }} />
				</IconButton>
				<IconButton aria-label="Decrease text size" onClick={(e) => { e.stopPropagation(); decreaseSize(); }} size="small" sx={iconButtonSx}>
					<ZoomOutIcon sx={{ fontSize: 16 }} />
				</IconButton>
				<IconButton aria-label="Reset text size" onClick={(e) => { e.stopPropagation(); resetSize(); }} size="small" sx={iconButtonSx}>
					<RefreshIcon sx={{ fontSize: 16 }} />
				</IconButton>
			</Box>

			<Divider />

			<Box
				onClick={handleStopPropagation}
				sx={{
					maxHeight: 280,
					overflowY: "auto",
					overflowX: "hidden",
					px: 1.5,
					py: 1.25,
					WebkitOverflowScrolling: "touch",
				}}
			>
				<Typography
					component="div"
					sx={{
						fontSize: `${tooltipSize}px`,
						lineHeight: 1.55,
						fontFamily: '"manrope-reg", sans-serif',
						color: "text.primary",
						whiteSpace: "pre-wrap",
						wordBreak: "break-word",
					}}
				>
					{text}
				</Typography>
			</Box>
		</Box>
	);
}
